import { useCallback, useEffect, useRef, useState } from "react";
import {
  api,
  runTurn,
  type CaseView,
  type Language,
  type Message,
  type PackView,
  type TenantView,
} from "./api";
import { accentFor, applyAccent, applyTheme, chatStyle, prefersDark, type Theme } from "./theme";
import { useHomeHref } from "./session";
import { Bubble, Header } from "./components/chrome";
import { CraigFace } from "./components/CraigFace";
import { LetCraigDoIt, Wordmark } from "./components/Brand";
import { BusinessPage } from "./components/BusinessPage";
import { ChatModules } from "./components/ChatModules";
import { Declaration } from "./components/Declaration";
import { ReviewSheet } from "./components/ReviewSheet";
import { DropZone } from "./components/DropZone";
import { FileDrawer } from "./components/FileDrawer";
import { LanguagePicker } from "./components/LanguagePicker";
import { Presenter } from "./components/Presenter";
import { SecureInline } from "./components/SecureInline";
import { StreamedMd } from "./components/typography";

interface Turn {
  role: "client" | "craig";
  text: string;
  secureLabel?: string;
}

/** Per tenant, so someone using two practices doesn't cross the wires. */
const resumeKey = (slug: string) => `craig:case:${slug}`;

/**
 * Our own setup conversation, told apart from a real business's intake.
 *
 * Keyed on the pack rather than the slug so renaming the setup tenant
 * cannot quietly turn a practice's intake into ours. Three things branch on
 * it and they must agree: whether the conversation resumes, whether its
 * pointer is even written, and whether the way out of it is shown.
 */
const SETUP_PACK = "craig-onboarding";

/**
 * `language` comes from `?lang=` on the URL, and skips the picker.
 *
 * It exists so a business can hand out a link that opens in the right
 * language already — `/riverside?lang=vi` in a Vietnamese newsletter, say.
 * Whoever set that link has already answered the question on the reader's
 * behalf, and asking it again would undo the gesture.
 *
 * Nothing on our own pages sets it. The landing page briefly did, and the
 * result was being asked to choose a language twice in ten seconds. Anyone
 * arriving under their own steam gets the picker, which is the first thing
 * on the screen and reads as crAIg asking rather than a form gating.
 */
export function Intake({ slug, language }: { slug: string; language?: string }) {
  const [tenant, setTenant] = useState<TenantView | null>(null);
  const [pack, setPack] = useState<PackView | null>(null);
  const [languages, setLanguages] = useState<(Language & { search?: string })[]>([]);
  const [voiceAvailable, setVoiceAvailable] = useState(false);
  const [bootError, setBootError] = useState<string | null>(null);

  const [kase, setCase] = useState<CaseView | null>(null);
  const [thread, setThread] = useState<Turn[]>([]);
  const [streaming, setStreaming] = useState("");
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [inputHint, setInputHint] = useState<string | null>(null);
  const [dateValue, setDateValue] = useState("");
  const [secureRequest, setSecureRequest] = useState<string | null>(null);
  const [savingSecure, setSavingSecure] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  const [theme, setTheme] = useState<Theme>(() => (prefersDark() ? "dark" : "light"));
  const [wide, setWide] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [fileTouched, setFileTouched] = useState(0);
  const [dropOpen, setDropOpen] = useState(false);
  // The drop-zone opens itself exactly once. Re-opening it every time the
  // case updates would trap someone who deliberately closed it.
  const dropOffered = useRef(false);

  const threadBox = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /*
    Is this crAIg setting up a business, or a business's own customer?

    Keyed on the pack rather than the slug, so renaming the setup tenant
    can't quietly put a link to our sales page on a real practice's intake.
    Only the former gets a way out of the conversation — see Header.

    It has to be worked out up here, before the loading return below, so
    the session lookup that decides where "out" goes is a hook like any
    other. On a real practice's page it is false and no request is made.
  */
  const ourOwnSetup = pack?.id === SETUP_PACK;
  const homeHref = useHomeHref(ourOwnSetup);

  /* ── Theme and branding ───────────────────────────────────────────────── */

  useEffect(() => {
    applyTheme(theme);
    // Re-applied on theme change: the same brand colour needs different
    // treatment to stay legible on a light versus a dark surface.
    // Through the scheme, so a business that pointed at 'Ocean' gets it.
    applyAccent(accentFor(tenant?.branding), theme);
  }, [theme, tenant]);

  /* ── Boot ─────────────────────────────────────────────────────────────── */

  useEffect(() => {
    Promise.all([api.tenant(slug), api.languages()])
      .then(async ([t, l]) => {
        setTenant(t.tenant);
        setPack(t.pack);
        setLanguages(l.languages as (Language & { search?: string })[]);
        setVoiceAvailable(l.voiceAvailable);

        /*
          A setup conversation always starts fresh.

          The opposite of what a customer's intake wants, and deliberately
          so, because the two are used completely differently. A customer
          opens their plumber's link once and works through it; coming back
          to a lost conversation is the whole reason resuming exists.

          A business opens OUR setup page repeatedly and means something new
          each time — the dashboard sends them here for every chat they add,
          and a chat is exactly the thing this conversation produces. Resume
          it and "add a chat" silently reopens the last one, which is right
          approximately never. There is no half-finished setup worth
          inheriting either: what they abandoned last week described a
          different chat from the one they came back to build.

          The key is cleared rather than merely ignored, so a conversation
          started before this shipped doesn't sit in local storage forever.
        */
        if (t.pack.id === SETUP_PACK) {
          localStorage.removeItem(resumeKey(slug));
          return;
        }

        /*
         * Resume where they left off. Drop-off is the biggest risk to a long
         * intake, and the ordinary cause is closing the tab — to find a
         * receipt, because the bus came. Losing forty turns to that would be
         * indefensible.
         */
        const saved = localStorage.getItem(resumeKey(slug));
        if (!saved) return;
        try {
          const resumed = await api.getCase(saved);
          /*
            A finished one is not a conversation to come back to — a
            customer whose job was closed weeks ago should not reopen it.

            Only "closed": a case they have signed but not yet had acted on
            is still theirs to pick up, and resuming that is the point.
          */
          if (resumed.case.status === "closed") {
            localStorage.removeItem(resumeKey(slug));
            return;
          }
          setCase(resumed.case);
          setPack(resumed.pack);
          setThread(
            resumed.transcript.map((m: Message) => ({
              role: m.role,
              text: m.text,
              ...(m.secureLabel ? { secureLabel: m.secureLabel } : {}),
            })),
          );
          document.documentElement.dir =
            l.languages.find((x) => x.code === resumed.case.language)?.dir ?? "ltr";
        } catch {
          // Deleted, or from another environment. Not worth an error screen.
          localStorage.removeItem(resumeKey(slug));
        }
      })
      .catch((e: Error) => setBootError(e.message));
  }, [slug]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const apply = () => {
      setWide(mq.matches);
      setDrawerOpen(mq.matches);
    };
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    document.body.style.overflow = drawerOpen && !wide ? "hidden" : "";
    return () => void (document.body.style.overflow = "");
  }, [drawerOpen, wide]);

  useEffect(() => {
    const el = threadBox.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread, streaming, busy, suggestions]);

  useEffect(() => {
    if (!busy) inputRef.current?.focus({ preventScroll: true });
  }, [busy, secureRequest]);

  useEffect(() => {
    if (drawerOpen) setFileTouched(0);
  }, [drawerOpen]);

  /*
   * Open the drop-zone the moment the questions are done.
   *
   * crAIg says "pop your receipts in the window that's just opened" — so a
   * window has to actually open. Telling someone a panel exists somewhere
   * on the right is a sentence people don't act on, and it leaves crAIg
   * describing something that didn't happen.
   */
  useEffect(() => {
    if (!kase?.documentsUnlocked || dropOffered.current || kase.documents.length > 0) return;
    dropOffered.current = true;
    setDropOpen(true);
  }, [kase?.documentsUnlocked, kase?.documents.length]);

  /* ── Talking ──────────────────────────────────────────────────────────── */

  const send = useCallback(async (text: string, caseId: string) => {
    setBusy(true);
    setStreaming("");
    setSuggestions([]);
    setInputHint(null);
    setDateValue("");
    setSpeaking(true);

    let acc = "";
    await runTurn(caseId, text, {
      onDelta(chunk) {
        acc += chunk;
        setStreaming(acc);
      },
      onDone(result) {
        setThread((t) => [...t, { role: "craig", text: result.say }]);
        setStreaming("");
        setSuggestions(result.suggestions);
        setInputHint(result.inputHint);
        setSecureRequest(result.secureRequest);
        setCase(result.case);
        setFileTouched((n) => n + 1);
        // crAIg asked for a specific document. Open the drop zone on it
        // rather than making them find the button he just mentioned.
        if (result.opened?.includes("fileSlots")) setDropOpen(true);
        /* crAIg has just put a document on their screen. Nothing to open
           here — it renders from the case itself, above the composer, and
           stays there until they agree. It used to open into nothing at
           all: the module returned `open: true` and no component read it. */
      },
      onError(message) {
        setThread((t) => [...t, { role: "craig", text: message }]);
        setStreaming("");
      },
    });

    setBusy(false);
    setTimeout(() => setSpeaking(false), 500);
  }, []);

  const start = useCallback(
    async (languageCode: string) => {
      if (!tenant) return;
      setBusy(true);
      try {
        const { case: created, pack: p } = await api.createCase(tenant.slug, languageCode);
        // Not for a setup conversation — nothing reads it back, and writing
        // a pointer we will never follow is how one gets followed by
        // accident later. See the boot effect.
        if (p.id !== SETUP_PACK) {
          localStorage.setItem(resumeKey(tenant.slug), created.id);
        }
        setCase(created);
        setPack(p);
        document.documentElement.dir =
          languages.find((l) => l.code === languageCode)?.dir ?? "ltr";
        await send("", created.id);
      } catch (e) {
        setBootError((e as Error).message);
        setBusy(false);
      }
    },
    [tenant, languages, send],
  );

  /* Someone who picked their language on the landing page has already
     answered the only question the opening screen asks, so open straight
     into the conversation.

     Guarded by a ref rather than the case state because StrictMode invokes
     effects twice in development, and two `createCase` calls would leave an
     orphan case and a very confusing first turn. */
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!language || !tenant || kase || busy || autoStarted.current) return;
    autoStarted.current = true;
    void start(language);
  }, [language, tenant, kase, busy, start]);

  const say = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !kase || busy) return;
      setThread((t) => [...t, { role: "client", text: trimmed }]);
      setInput("");
      void send(trimmed, kase.id);
    },
    [kase, busy, send],
  );

  const saveSecure = useCallback(
    async (values: Record<string, string>) => {
      if (!kase || !secureRequest || !pack) return;
      const group = pack.secureGroups.find((g) => g.id === secureRequest);
      setSavingSecure(true);
      try {
        const { case: updated } = await api.saveSecure(kase.id, secureRequest, values);
        setCase(updated);
        // A masked marker in the thread, never the value itself.
        setThread((t) => [...t, { role: "client", text: "", secureLabel: group?.title ?? "Saved" }]);
        setSecureRequest(null);
        await send("", updated.id);
      } finally {
        setSavingSecure(false);
      }
    },
    [kase, secureRequest, pack, send],
  );

  const correct = useCallback(
    async (name: string, value: string) => {
      if (!kase) return;
      const { case: updated } = await api.correctField(kase.id, { [name]: value });
      setCase(updated);
    },
    [kase],
  );

  /* ── Render ───────────────────────────────────────────────────────────── */

  if (bootError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="card px-5 py-4 max-w-sm">
          <p style={{ fontSize: 14 }}>{bootError}</p>
          <p style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 6 }}>
            Check the link you were given, or get in touch with whoever sent it.
          </p>
        </div>
      </div>
    );
  }

  if (!tenant || !pack) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CraigFace size={44} thinking />
      </div>
    );
  }

  const started = Boolean(kase);
  const secureGroup = secureRequest ? pack.secureGroups.find((g) => g.id === secureRequest) : null;

  /* Non-null only on OUR setup conversation — the server decides, and sends
     null for every real customer. Null and empty differ: null means render
     nothing at all, empty means "a setup, but nothing settled yet", which
     gets its own line rather than an empty box. */
  const willHave = kase?.willHave ?? null;

  /* `ourOwnSetup` is worked out above, with the hooks.

     Safe to leave and come back to either way: the case lives on the
     server and the pointer is in localStorage, so returning resumes it. */

  /*
    The conversation, lifted out so it can be PLACED rather than fixed.

    A business with a page of their own gets it sitting inside that page;
    everyone else gets it filling the screen as before. Same component,
    same state, same case — only where it sits changes, which is the only
    way to have both without two conversations to keep in step.
  */
  const conversation = (
      <div
      className="card chat-look flex flex-col flex-1 min-w-0 overflow-hidden"
      /* The business's own surface, font and bubbles — scoped here rather
         than on :root, so their choices dress their conversation and
         nothing else. See chatStyle. */
      style={{ minHeight: "min(78vh, 640px)", ...chatStyle(tenant.branding.chat) }}
    >
        {/*
          role="log" with a polite live region so a screen reader announces
          crAIg's replies as they arrive. Without it the conversation is
          silent to anyone not watching the screen — and "answer in your own
          language, however you find it easiest" has to include them.
          Polite rather than assertive: it should wait for a pause, not
          interrupt someone mid-sentence.
        */}
        <div
          ref={threadBox}
          role="log"
          aria-live="polite"
          aria-relevant="additions text"
          aria-label="Conversation"
          className="flex-1 scroll-y px-4 sm:px-5 py-5 space-y-3.5"
          style={{ maxHeight: "min(78vh, 640px)" }}
        >
          {!started && (
            <div className="flex flex-col items-center justify-center h-full py-8">
              {/*
                A business may put its own name and face on him — see
                AssistantConfig. Their customer came to deal with them, and
                "Hi, I'm Sparky" from a business called Sparky Electrical
                reads as theirs rather than as software they bought.

                What it never changes is what he is. The greeting is his
                voice, not a disguise: he still says he's an assistant when
                it matters, still doesn't advise, and the sign-off at the
                bottom of the file panel still says who made him.
              */}
              {pack.assistant?.avatar ? (
                <span aria-hidden="true" style={{ fontSize: 46, lineHeight: 1 }}>
                  {pack.assistant.avatar}
                </span>
              ) : (
                <CraigFace size={52} brand={tenant.branding.face === "brand"} />
              )}
              <h1
                className="mt-4"
                style={{ fontSize: 22, fontWeight: 650, letterSpacing: "-0.02em" }}
              >
                {pack.assistant?.name ? (
                  <>Hi, I'm {pack.assistant.name}</>
                ) : (
                  <>
                    Hi, I'm <Wordmark size={22} />
                  </>
                )}
              </h1>
              <p style={{ color: "var(--ink-2)", fontSize: 14.5, marginTop: 4, marginBottom: 24 }}>
                {pack.assistant?.greeting || pack.copy.invitation}
              </p>
              <LanguagePicker
                languages={languages}
                onPick={start}
                showVoiceBadges={voiceAvailable}
                {...(pack.copy.languagePrompt ? { prompt: pack.copy.languagePrompt } : {})}
                {...(pack.copy.languageNote ? { note: pack.copy.languageNote } : {})}
              />
            </div>
          )}

          {thread.map((m, i) =>
            m.secureLabel ? (
              <Bubble key={i} from="you">
                <span className="flex items-center gap-1.5">
                  <span aria-hidden="true">🔒</span>
                  <span>{m.secureLabel} saved</span>
                </span>
              </Bubble>
            ) : (
              <Bubble
                key={i}
                from={m.role === "client" ? "you" : "craig"}
                speaking={speaking && i === thread.length - 1}
              >
                {m.role === "client" ? m.text : <StreamedMd text={m.text} />}
              </Bubble>
            ),
          )}

          {streaming && (
            <Bubble from="craig" speaking>
              <StreamedMd text={streaming} streaming />
            </Bubble>
          )}

          {busy && !streaming && started && (
            <div className="flex items-center gap-2.5">
              <CraigFace size={30} thinking />
              <span style={{ fontSize: 13, color: "var(--ink-3)" }} role="status">
                thinking…
              </span>
            </div>
          )}

          {suggestions.length > 0 && !busy && !secureRequest && (
            <div className="flex flex-wrap gap-2 pl-0 sm:pl-10">
              {suggestions.map((s, i) => (
                <button
                  key={s + i}
                  type="button"
                  onClick={() => say(s)}
                  className="chip fade-up"
                  style={{ animationDelay: `${i * 0.04}s` }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {/*
          What their chat will be able to do — the setup conversation only,
          and never a customer's (the server sends null for every other
          pack). On a phone it sits here, immediately above the composer,
          because that is where the eye already is between turns and a chip
          arriving needs to be noticed without being announced.
        */}
        {/*
          Something to read, when crAIg has put one up.

          Directly above the composer and outside the scrolling thread, so
          it cannot be scrolled past and lost — the whole claim this
          surface supports is that they saw it. It stays after they agree,
          quietly, because "what did I sign up to?" is a fair question ten
          minutes later.
        */}
        {kase?.presenter && (
          <div className="px-4 sm:px-5 pt-3">
            <Presenter
              caseId={kase.id}
              presenter={kase.presenter}
              onAgreed={(updated) => setCase(updated)}
            />
          </div>
        )}

        {willHave && !wide && <ChatModules modules={willHave} wide={false} />}

        {/*
          A case this business can't take is finished, so the composer goes.
          Leaving a live text box under a "we can't help with this" message
          invites someone to keep typing into a conversation that has ended,
          and crAIg would have to turn them down twice.
        */}
        {started && kase?.declined ? (
          <div
            className="px-4 sm:px-5 py-4 safe-b"
            style={{ borderTop: "1px solid var(--line)", background: "var(--surface-2)" }}
          >
            <p style={{ fontSize: 13.5, color: "var(--ink-2)", margin: 0 }}>
              That's everything from us on this one — nothing further is needed, and nobody will
              be in touch. Thanks for your time.
            </p>
          </div>
        ) : started ? (
          <div
            className="px-4 sm:px-5 py-3 safe-b"
            style={{ borderTop: "1px solid var(--line)", background: "var(--surface-2)" }}
          >
            {secureGroup ? (
              <SecureInline
                group={secureGroup}
                reviewerNoun={pack.copy.reviewerNoun}
                saving={savingSecure}
                onSave={saveSecure}
                onSkip={() => {
                  setSecureRequest(null);
                  say("I'll do that part later");
                }}
              />
            ) : inputHint === "date" ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={dateValue}
                    onChange={(e) => setDateValue(e.target.value)}
                    max={new Date().toISOString().slice(0, 10)}
                    min="1900-01-01"
                    className="field"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!dateValue) return;
                      const [y, m, d] = dateValue.split("-");
                      // Read back the way it's said locally.
                      say(`${d}/${m}/${y}`);
                    }}
                    disabled={busy || !dateValue}
                    className="btn btn-primary"
                  >
                    Send
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setInputHint(null)}
                  style={{
                    fontSize: 12.5,
                    color: "var(--ink-3)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  Type my answer instead
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  type="text"
                  inputMode={
                    inputHint === "email"
                      ? "email"
                      : inputHint === "tel"
                        ? "tel"
                        : inputHint === "number"
                          ? "decimal"
                          : "text"
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") say(input);
                  }}
                  placeholder="Type your answer, or ask a question…"
                  className="field"
                />
                <button
                  type="button"
                  onClick={() => say(input)}
                  disabled={busy || !input.trim()}
                  className="btn btn-primary"
                >
                  Send
                </button>
              </div>
            )}
          </div>
        ) : null}
      </div>

  );

  return (
    <div className="min-h-screen flex flex-col">
      <Header
        practiceName={tenant.displayName}
        {...(tenant.branding.tagline ? { tagline: tenant.branding.tagline } : {})}
        {...(tenant.branding.logoUrl ? { logoUrl: tenant.branding.logoUrl } : {})}
        {...(kase ? { progress: kase.progress } : {})}
        {...(ourOwnSetup
          ? { homeHref, homeLabel: homeHref === "/dashboard" ? "Dashboard" : "Home" }
          : {})}
        theme={theme}
        onToggleTheme={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
        onRestart={() => {
          if (!confirm("Start over? Your answers so far will be cleared.")) return;
          // Forgets only the local pointer — the case survives on the server,
          // so a mis-tap can't destroy someone's work.
          localStorage.removeItem(resumeKey(slug));
          location.reload();
        }}
      />

      {/*
        A person has been asked for. Shown as a standing reassurance rather
        than left in the transcript, because by the time someone has scrolled
        past four more questions they no longer know whether it was heard.
        Never says why — the reason was written for the business, and reading
        "customer sounds distressed" about yourself would be horrible.
      */}
      {kase?.escalation && (
        <div
          role="status"
          className="w-full max-w-6xl mx-auto px-4 sm:px-6 pt-3"
          style={{ fontSize: 13.5 }}
        >
          <div
            className="card px-4 py-3"
            style={{ background: "var(--warn-tint)", borderColor: "var(--warn)" }}
          >
            {kase.escalation.urgency === "now"
              ? `Someone at ${tenant.displayName} has been alerted and will contact you as soon as they can.`
              : `I've asked someone at ${tenant.displayName} to get in touch with you about this.`}
          </div>
        </div>
      )}

      <main className="flex-1 w-full">
        {pack.page ? (
          <BusinessPage tenant={tenant} page={pack.page} chat={conversation} />
        ) : (
          <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 py-5 flex gap-6 items-start">
            {conversation}
            {/* Wide enough to just show it, so no tapping required. */}
            {willHave && wide && <ChatModules modules={willHave} wide />}
          </div>
        )}
        {kase && (
          <FileDrawer
            kase={kase}
            pack={pack}
            wide={wide}
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            onCorrect={correct}
            onOpenDrop={() => setDropOpen(true)}
          >
            {/*
              The check comes before the declaration, and replaces it until
              it is done. Showing both at once would let someone sign
              without reading, which is the outcome this exists to prevent —
              and `needsReview` returns if they change anything afterwards,
              so a confirmation can never come to cover data it didn't.
            */}
            {kase.needsReview ? (
              <ReviewSheet
                kase={kase}
                pack={pack}
                onReviewed={setCase}
                onCorrect={(ref) => {
                  setDrawerOpen(true);
                  // Let the drawer open before jumping, or the row isn't
                  // in the document yet and the scroll goes nowhere.
                  requestAnimationFrame(() =>
                    document
                      .querySelector(`[data-field="${ref}"]`)
                      ?.scrollIntoView({ behavior: "smooth", block: "center" }),
                  );
                }}
              />
            ) : (
              <Declaration kase={kase} pack={pack} onSigned={setCase} />
            )}
          </FileDrawer>
        )}
      </main>

      {kase && !drawerOpen && (
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open your details"
          className={`fixed right-4 bottom-24 z-40 btn btn-quiet ${fileTouched ? "pulse-ring" : ""}`}
          style={{ boxShadow: "var(--shadow)", borderRadius: 999 }}
        >
          Your details · {kase.progress}%
        </button>
      )}
      {kase && drawerOpen && !wide && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setDrawerOpen(false)}
          style={{ background: "rgba(20,20,18,.4)" }}
          aria-hidden="true"
        />
      )}

      {kase && (
        <DropZone
          open={dropOpen}
          kase={kase}
          pack={pack}
          firstName={(kase.fields.name ?? "").split(/\s+/)[0] ?? ""}
          onClose={() => {
            setDropOpen(false);
            // Let crAIg pick the thread back up rather than leaving the
            // customer looking at a closed window wondering what's next.
            if (!busy) void send("", kase.id);
          }}
          onUpdated={setCase}
        />
      )}

      {!started && <LetCraigDoIt />}
    </div>
  );
}
